import type { ErpLoadListQuantityData, UpdateErpLoadListQuantityInput } from "~/gql/graphqlBase.generated";

export type QuantityWorkState = "failed" | "pending" | "sending" | "unknown";

export type QuantityWork = "failed" | "none" | "pending" | "saving" | "unknown";

export interface QuantityCell {
	erpItemId: string;
	routeCode: string;
	upc: string;
}

export interface QuantityJournalEntry extends QuantityCell {
	containerCode: string;
	containerUtilization: number;
	generation: number;
	quantity: number;
	sessionId: string;
	sessionName: string;
	state: QuantityWorkState;
}

export interface SentQuantityBatch {
	generations: number[];
	sessionId: string;
}

export interface RetainedSessionWork {
	count: number;
	sessionId: string;
	sessionName: string;
}

const quantityCellKey = ({ erpItemId, routeCode, upc }: QuantityCell): string =>
	`${erpItemId}\u0000${upc}\u0000${routeCode}`;

const isSameCell = (left: QuantityCell, right: QuantityCell): boolean =>
	left.erpItemId === right.erpItemId && left.upc === right.upc && left.routeCode === right.routeCode;

const isSentEntry = (entry: QuantityJournalEntry, batch: SentQuantityBatch): boolean =>
	batch.generations.includes(entry.generation);

const toSentBatch = (sessionId: string, entries: QuantityJournalEntry[]): SentQuantityBatch => ({
	generations: entries.map((entry) => entry.generation),
	sessionId
});

const toQuantityInput = ({
	containerCode,
	containerUtilization,
	erpItemId,
	quantity,
	routeCode,
	upc
}: QuantityJournalEntry): UpdateErpLoadListQuantityInput => ({
	containerCode,
	containerUtilization,
	erpItemId,
	quantity,
	routeCode,
	upc
});

const savedQuantityOf = (saved: ErpLoadListQuantityData[], cell: QuantityCell): number =>
	saved.find((quantity) => isSameCell(quantity, cell))?.quantity ?? 0;

const cellWorkState = (entries: QuantityJournalEntry[], cell: QuantityCell): QuantityWorkState | undefined =>
	entries.find((entry) => isSameCell(entry, cell))?.state;

const isUnsavedWork = (state: QuantityWorkState | undefined): boolean => state === "failed" || state === "unknown";

const summarizeQuantityWork = (entries: QuantityJournalEntry[]): QuantityWork => {
	if (entries.some((entry) => entry.state === "failed")) return "failed";
	if (entries.some((entry) => entry.state === "unknown")) return "unknown";
	if (entries.some((entry) => entry.state === "sending")) return "saving";
	return entries.length > 0 ? "pending" : "none";
};

const retainedSessionWork = (journal: QuantityJournalEntry[], currentSessionId: string): RetainedSessionWork[] => {
	const bySession = new Map<string, RetainedSessionWork>();
	for (const entry of journal) {
		if (entry.sessionId === currentSessionId) continue;
		const retained = bySession.get(entry.sessionId);
		if (retained) retained.count += 1;
		else bySession.set(entry.sessionId, { count: 1, sessionId: entry.sessionId, sessionName: entry.sessionName });
	}
	return [...bySession.values()];
};

const quantityJournal = {
	cellWorkState,
	isSameCell,
	isSentEntry,
	isUnsavedWork,
	quantityCellKey,
	retainedSessionWork,
	savedQuantityOf,
	summarizeQuantityWork,
	toQuantityInput,
	toSentBatch
} as const;

export default quantityJournal;
